import { utc } from "moment";

import { type MarketPair, type UnderlyingSymbol } from "#/types/markets";
import { type OptionType } from "#/types/options";
import { type InstrumentType } from "#/types/trade";

export const isCallOptionType = (
  optionTypeOrInstrument?: OptionType | string,
) => {
  if (!optionTypeOrInstrument) return false;
  if (optionTypeOrInstrument === "call" || optionTypeOrInstrument === "put") {
    return optionTypeOrInstrument === "call";
  }
  return optionTypeOrInstrument.includes("-C");
};

export const isOptionInstrument = (
  typeOrInstrumentName: InstrumentType | string,
) => {
  return (
    typeOrInstrumentName !== "perps" &&
    !/^[A-Z0-9_]+_[A-Z0-9_]+-PERPETUAL$/.test(typeOrInstrumentName)
  );
};

const maturityToUnix = (maturity: string) => {
  const date = utc(maturity, "DDMMMYY").add(8, "hours");
  const ts = date.unix();
  return ts;
};

export const getOptionInstrumentProperties = (instrumentName: string) => {
  const [marketPair, maturity, _strike, optionTypeKey] =
    instrumentName.split("-");
  const [underlying] = marketPair.split("_");

  const strike = Number(_strike);
  const maturityTS = maturityToUnix(maturity);
  const optionType: OptionType = optionTypeKey === "C" ? "call" : "put";

  return {
    strike,
    maturity,
    marketPair: marketPair as MarketPair,
    underlying: underlying as UnderlyingSymbol,
    maturityTS,
    optionType,
  };
};

export const getPerpInstrumentProperties = (instrumentName: string) => {
  const [marketPair] = instrumentName.split("-");
  const [underlying] = marketPair.split("_");

  return {
    marketPair: marketPair as MarketPair,
    underlying: underlying as UnderlyingSymbol,
  };
};

export const createOptionInstrumentName = ({
  isCall,
  optionType,
  underlying,
  strike,
  maturity,
}: {
  isCall?: boolean;
  optionType?: OptionType;
  underlying: UnderlyingSymbol;
  strike: number;
  maturity: string;
}) => {
  const isCallOpt = isCall ?? isCallOptionType(optionType);
  const optionTypeKey = isCallOpt ? "C" : "P";

  return `${String(underlying)}_USDC-${maturity}-${strike}-${optionTypeKey}`;
};

export const createPerpInstrumentName = (
  underlyingOrPair: UnderlyingSymbol | MarketPair,
) => {
  const underlyingOrPairString = String(underlyingOrPair);
  const prefix = underlyingOrPairString.endsWith("_USDC")
    ? underlyingOrPairString
    : `${underlyingOrPairString}_USDC`;

  return `${prefix}-PERPETUAL`;
};

/**
 * Formats the instrument name by removing the trailing '_USDC' substring.
 *
 * @param instrumentName - The original instrument name string.
 * @returns The formatted instrument name without the '_USDC' suffix.
 *
 * @remarks
 * This method should only be used at the very end level when displaying the instrument name to the user.
 * It is not intended for any internal use cases or business logic.
 */
export const formatInstrumentName = (instrumentName: string) => {
  return instrumentName.replace(/_USDC/, "");
};

export const getInstrumentProperties = (instrument: string) => {
  return isOptionInstrument(instrument)
    ? getOptionInstrumentProperties(instrument)
    : getPerpInstrumentProperties(instrument);
};
